// "Just do it" agent tasks in chat — pure helpers for the plan card and the
// live progress view (AgentTaskCard.jsx). No React, no fetch: unit-tested.

export const BUDGETS = [100, 200, 500, 1000, 2500, 5000]; // cents — same scale in chat and on Agent tasks
export const POLL_MS = 3000;
export const AUTO_PLAN_MS = 15 * 60 * 1000; // a hand-off this fresh plans itself
export const MAX_STEPS = 10;
const TERMINAL = new Set(["done", "failed", "cancelled", "expired", "capped_steps", "capped_dollars", "capped_tools"]);
const FINAL = new Set(["done", "skipped", "blocked"]);

export function money(cents) {
  const c = Math.max(0, Number(cents) || 0);
  return c % 100 === 0 ? "$" + c / 100 : "$" + (c / 100).toFixed(2);
}

export function isTerminal(status) { return TERMINAL.has(status); }

export function shouldAutoPlan(task, now = Date.now()) {
  return !!(task && !task.plan && !task.runId && !task.dismissed && Number(task.at) > 0 && now - Number(task.at) < AUTO_PLAN_MS);
}

export function firstSentence(goal) {
  return String(goal || "").split(/[.\n?!]/)[0].replace(/\s+/g, " ").trim().slice(0, 80) || "Agent task";
}

// Editable copy of a server plan: plain strings for the inputs.
export function toDraft(plan) {
  const p = plan || {};
  return {
    title: p.title || "",
    summary: p.summary || "",
    steps: (Array.isArray(p.steps) ? p.steps : []).map((s) => ({ title: s.title || "", detail: s.detail || "" })),
    questions: (Array.isArray(p.questions) ? p.questions : []).map((q) => ({ q: q.q || "", a: q.a || "" })),
    deliverables: Array.isArray(p.deliverables) ? p.deliverables.slice() : [],
    approvals: Array.isArray(p.approvals) ? p.approvals.slice() : [],
    size: p.size || "medium",
    feasible: p.feasible !== false,
    reason: p.reason || "",
    fallback: !!p.fallback,
  };
}

// Draft → the plan the run is created with. Empty steps drop out; edited
// steps lose a stale detail line only if the user cleared the title.
export function draftToPlan(d) {
  const steps = (d.steps || []).map((s) => ({ title: String(s.title || "").trim(), detail: String(s.detail || "").trim() }))
    .filter((s) => s.title).slice(0, MAX_STEPS);
  return {
    title: String(d.title || "").trim(),
    summary: d.summary || "",
    steps,
    questions: (d.questions || []).map((q) => ({ q: q.q, a: String(q.a || "").trim() })),
    deliverables: d.deliverables || [],
    approvals: d.approvals || [],
    size: d.size || "medium",
    feasible: d.feasible !== false,
    reason: d.reason || "",
  };
}

export function stepMoves(steps, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= steps.length) return steps;
  const out = steps.slice();
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

export function progressOf(plan, status) {
  const steps = plan && Array.isArray(plan.steps) ? plan.steps : [];
  const count = (st) => steps.filter((s) => s.status === st).length;
  const open = steps.filter((s) => !FINAL.has(s.status));
  const running = !isTerminal(status) && status !== "paused";
  return {
    total: steps.length,
    done: count("done"),
    blocked: count("blocked"),
    skipped: count("skipped"),
    open: open.length,
    current: running && open.length ? open[0].n : null,
  };
}

// One line for the card header.
export function statusLine(run, plan) {
  const st = run && run.status;
  const p = progressOf(plan, st);
  const tally = p.total ? `${p.done} of ${p.total} checkpoints done` + (p.blocked ? `, ${p.blocked} blocked` : "") + (p.skipped ? `, ${p.skipped} skipped` : "") : "";
  if (st === "pending") return "Starting…";
  if (st === "running") return p.total ? `Working on checkpoint ${p.current || p.total} of ${p.total}` : "Working…";
  if (st === "paused" || st === "paused_user" || st === "paused_budget") return "Paused" + (tally ? " — " + tally : "");
  if (st === "done") return (p.open ? "Finished with open checkpoints" : "Finished") + (tally ? " — " + tally : "");
  if (st === "capped_dollars") return `Stopped — reached the ${money(run.dollar_cap_cents)} budget` + (tally ? " · " + tally : "");
  if (st === "capped_steps" || st === "capped_tools") return "Stopped — reached the step limit" + (tally ? " · " + tally : "");
  if (st === "cancelled") return "Stopped by you" + (tally ? " · " + tally : "");
  if (st === "expired") return "Expired";
  if (st === "failed") return "Stopped — something went wrong" + (tally ? " · " + tally : "");
  return st || "";
}

const PROGRESS = "[progress]";
export function latestActivity(steps) {
  const list = Array.isArray(steps) ? steps : [];
  for (let i = list.length - 1; i >= 0; i--) {
    const s = list[i];
    if (s && s.role === "system" && typeof s.content === "string" && s.content.startsWith(PROGRESS)) {
      const t = s.content.slice(PROGRESS.length).trim();
      return t ? t[0].toUpperCase() + t.slice(1) : "";
    }
  }
  return "";
}

// The agent's last reply minus protocol noise = the report.
export function finalReport(steps) {
  const list = Array.isArray(steps) ? steps : [];
  for (let i = list.length - 1; i >= 0; i--) {
    const s = list[i];
    if (!s || s.role !== "assistant" || typeof s.content !== "string") continue;
    const text = s.content
      .replace(/<<<TOOL:[\s\S]*?<<<END>>>/g, "")
      .replace(/<<<DONE>>>/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    if (text) return text.slice(0, 6000);
  }
  return "";
}

// Files worth handing back: what the agent made, not its inputs or notes.
export function deliverableFiles(files) {
  const rank = (p) => (/\.(docx|pdf|pptx|xlsx)$/i.test(p) ? 0 : /\.(md|csv|txt|json|html)$/i.test(p) ? 1 : 2);
  return (Array.isArray(files) ? files : [])
    .filter((f) => f && typeof f.path === "string" && !/^uploads\//.test(f.path) && !/\.notes\.md$/i.test(f.path))
    .sort((a, b) => rank(a.path) - rank(b.path) || a.path.localeCompare(b.path))
    .slice(0, 12);
}

export function fileUrl(runId, filePath) {
  return `/api/hermes/runs/${encodeURIComponent(runId)}/files/` + String(filePath).split("/").map(encodeURIComponent).join("/");
}

// Snapshot persisted on the chat message once the run ends, so the card
// still reads right after the run expires on the server.
export function resultSnapshot(detail) {
  const run = (detail && detail.run) || {};
  return {
    status: run.status || "",
    cost_cents: Number(run.cost_cents_used) || 0,
    dollar_cap_cents: Number(run.dollar_cap_cents) || 0,
    plan: run.plan || null,
    report: finalReport(detail && detail.steps),
    files: deliverableFiles(detail && detail.files).map((f) => ({ path: f.path, size_bytes: f.size_bytes || 0 })),
    at: Date.now(),
  };
}

// Tiny Markdown-ish renderer input: lines → blocks with **bold** runs.
export function reportBlocks(text) {
  return String(text || "").split("\n").map((line) => {
    const bullet = /^\s*[-*•]\s+/.test(line);
    const body = bullet ? line.replace(/^\s*[-*•]\s+/, "") : line;
    const runs = body.split(/(\*\*[^*]+\*\*)/).filter(Boolean).map((r) => (/^\*\*[^*]+\*\*$/.test(r) ? { b: true, t: r.slice(2, -2) } : { b: false, t: r }));
    return { bullet, runs, blank: !body.trim() };
  });
}

export function fileLabel(p) {
  return String(p || "").split("/").pop();
}
